import { Row, UploadProps, notification } from 'antd';
import FilePdfOutlined from '@ant-design/icons/FilePdfOutlined';

import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import { TextExtraSmall } from 'components/ui/base/typography';
import {
    DivInformationTemplateFirstRow,
    CloseIcon,
    PreviewIcon,
} from 'views/custom-declaration/products/bulk-upload/ListBulkUploadFiles.styles';
import Notification from 'components/ui/base/notification/Notification';
import { StyledDragger } from 'components/new-ui/base/dragger/styles';
import { FileTypes } from 'utils/file-types';

const { Text: AntText } = Typography;

export type BulkUploadUploadFunc = (
    options: Parameters<NonNullable<UploadProps<any>['customRequest']>>[0]
) => any | Promise<any>;
export type BulkUploadDownloadFunc = () => Promise<{ preSignedUrl: string } | null>;

export interface BulkUploadRef {
    triggerUpload: () => void;
}

type Props = {
    disabled?: boolean;
    onUpload?: BulkUploadUploadFunc;
    onDownload?: BulkUploadDownloadFunc;
    onDelete?: () => void;
    hasDontOverrideFirstRowText?: boolean;
    withNotification?: boolean;
    fileType?: FileTypes[] | FileTypes;
};

const BulkUpload = forwardRef<BulkUploadRef, Props>(
    (
        {
            disabled,
            onDownload,
            onUpload,
            onDelete,
            hasDontOverrideFirstRowText = false,
            withNotification = true,
            fileType,
        },
        ref
    ) => {
        const draggerContainerRef = useRef<HTMLDivElement | null>(null);

        const { t } = useTranslation('customs_declarations');

        useImperativeHandle(ref, () => ({
            triggerUpload: () => {
                const input = draggerContainerRef.current?.querySelector(
                    'input[type="file"]'
                ) as HTMLInputElement | null;
                if (input) {
                    input.click();
                } else {
                    console.warn('File input not found.');
                }
            },
        }));

        const [file, setFile] = useState<File>();

        const handleUpload = async (options: Parameters<BulkUploadUploadFunc>[0]) => {
            const fileExt = (options.file as File).name.split('.').at(-1)?.toLowerCase();

            if (fileType) {
                if (
                    // There is no file extension OR
                    !fileExt ||
                    // The file extension doesn't match the allowed file types OR
                    (typeof fileType === 'string' && fileType.toLowerCase() !== fileExt) ||
                    // The file extension doesn't match the allowed file types (Array)
                    (Array.isArray(fileType) && !fileType.map((type) => type.toLowerCase()).includes(fileExt))
                ) {
                    Notification({
                        type: 'error',
                        messageTitle: t('bulk-upload.file-extension-error-message.title'),
                        description: t('bulk-upload.file-extension-error-message.description'),
                    });
                    return;
                }
            }

            const response = await onUpload?.(options);

            if (withNotification) {
                if (response) {
                    Notification({
                        type: 'success',
                        messageTitle: t('bulk-upload.file-handle-success-message.title'),
                        description: t('bulk-upload.file-handle-success-message.description'),
                        placement: 'top',
                    });

                    setFile(options.file as File);
                } else {
                    Notification({
                        type: 'error',
                        messageTitle: t('bulk-upload.file-handle-error-message.title'),
                        description: t('bulk-upload.file-handle-error-message.description'),
                    });
                }
            }
        };

        const handleDownload = async () => {
            const response = await onDownload?.();

            if (response) {
                window.open(response.preSignedUrl);
            }
        };

        const handleDelete = async () => {
            setFile(undefined);
            notification.success({ message: 'File deleted successfully.' });
            onDelete?.();
        };

        return (
            <>
                {file ? (
                    <div>
                        <Row align="middle">
                            <CloseIcon onClick={handleDelete} />
                            <AntText>
                                <FilePdfOutlined /> {file?.name}
                            </AntText>
                        </Row>
                        <Row>
                            <PreviewIcon /> {t('bulk-upload.success-message')}
                        </Row>
                    </div>
                ) : (
                    <div ref={draggerContainerRef}>
                        {!disabled && (
                            <StyledDragger
                                id="file-upl"
                                maxCount={1}
                                customRequest={handleUpload}
                                showUploadList={false}
                            >
                                <p
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        justifyContent: 'center',
                                    }}
                                >
                                    <InboxOutlined
                                        style={{
                                            fontSize: '24px',
                                            color: 'var(--brand-7)',
                                        }}
                                    />
                                    Click or drag file to this area to upload
                                </p>
                            </StyledDragger>
                        )}
                    </div>
                )}

                {!disabled && (
                    <Row
                        style={{
                            display: 'flex',
                            gap: 8,
                            textAlign: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <AntText
                            style={{ color: 'var(--brand-6)', cursor: 'pointer' }}
                            onClick={() => !disabled && handleDownload()}
                            disabled={disabled}
                        >
                            <DownloadOutlined /> Spreadsheet Template
                        </AntText>
                    </Row>
                )}

                {hasDontOverrideFirstRowText && (
                    <DivInformationTemplateFirstRow>
                        <TextExtraSmall>{t('bulk-upload.information-template-first-row')}</TextExtraSmall>
                    </DivInformationTemplateFirstRow>
                )}
            </>
        );
    }
);

export default BulkUpload;
